/**
 * Motor de cálculo do simulador de Reforma Tributária.
 *
 * Replica, célula a célula, as fórmulas do Excel de referência
 * (FH_Simulador_Reforma_Tributaria), combinando:
 *  - dados REAIS extraídos do SPED (via useReformaFiscalData: /mensal e /creditos)
 *  - os parâmetros ajustáveis do Bloco E (via useReformaParams — "células amarelas")
 *
 * Convenção: parâmetros chegam em percentual (0-100), convertidos aqui para
 * fração (0-1) antes de qualquer conta.
 *
 * ⚠️ Premissas fixas do Excel (não são "células amarelas", por isso não
 * fazem parte de useReformaParams — mantidas como constantes para bater
 * exatamente com a planilha):
 *  - 10% do faturamento é considerado receita de serviços (base do ISS e do
 *    segmento "Oficina" no Impacto Caixa).
 *  - Mão de obra ~60% / Materiais ~40% do custo dos serviços de oficina.
 *  - PIS+COFINS atual de referência para comparação de crédito: 9,25%.
 */

const PCT_RECEITA_SERVICOS = 0.10; // fração fixa do faturamento total (ISS + segmento Oficina)
const PCT_MAO_DE_OBRA_OFICINA = 0.60;
const PCT_MATERIAIS_OFICINA = 0.40;
const ALIQ_PIS_COFINS_ATUAL_REF = 0.0925;
const PCT_DESPESAS_ELEGIVEIS = 0.80; // Excel B23: estimativa conservadora (fixa)

function frac(pct) {
  const n = Number(pct);
  return Number.isFinite(n) ? n / 100 : 0;
}

/**
 * Bloco A (Simulação) — Base Anual: faturamento, receitas e COGS por linha,
 * despesas elegíveis (real, via M105) e margem tributável CBS/IBS.
 */
export function calcBaseAnual(totalMensal, creditosDespesas, params) {
  const faturamento = totalMensal?.faturamento || 0;
  const pctVeiculos = frac(params.pctReceitaVeiculos);
  const pctCogsVeiculos = frac(params.pctCogsVeiculos);
  const pctCogsPecasServ = frac(params.pctCogsPecasServ);

  const receitaVeiculos = faturamento * pctVeiculos;
  const receitaPecasServ = faturamento * (1 - pctVeiculos);
  const cogsVeiculos = receitaVeiculos * pctCogsVeiculos;
  const cogsPecasServ = receitaPecasServ * pctCogsPecasServ;
  const cogsTotal = cogsVeiculos + cogsPecasServ;
  const despesasElegiveis = creditosDespesas || 0;
  const margemTributavel = faturamento - cogsTotal - despesasElegiveis;

  return {
    faturamento,
    receitaVeiculos,
    receitaPecasServ,
    cogsTotal,
    cogsVeiculos,
    cogsPecasServ,
    despesasElegiveis,
    margemTributavel,
  };
}

/**
 * Bloco B (Simulação) — Regime Atual: PIS/COFINS/ICMS líquidos reais (do
 * SPED, mesmos totais da aba Dados Mensais) + ISS estimado (10% faturamento
 * × alíquota ISS).
 */
export function calcRegimeAtual(totalMensal, baseAnual, params) {
  const pis = {
    debito: totalMensal?.pisDebito || 0,
    credito: totalMensal?.pisCredito || 0,
    liquido: totalMensal?.pisLiquido || 0,
  };
  const cofins = {
    debito: totalMensal?.cofDebito || 0,
    credito: totalMensal?.cofCredito || 0,
    liquido: totalMensal?.cofLiquido || 0,
  };
  const icms = {
    debito: totalMensal?.icmsSaidas || 0,
    credito: totalMensal?.icmsCreditos || 0,
    liquido: totalMensal?.icmsLiquido || 0,
  };
  const issValor = baseAnual.faturamento * PCT_RECEITA_SERVICOS * frac(params.issAtualServicos);
  const iss = { debito: issValor, credito: 0, liquido: issValor };

  const total = pis.liquido + cofins.liquido + icms.liquido + iss.liquido;

  return { pis, cofins, icms, iss, total };
}

/**
 * Bloco C (Simulação) — Regime Futuro Pleno (2033): CBS + IBS líquidos,
 * com crédito integral sobre COGS + despesas elegíveis. IS não aplica
 * (revendedora não recolhe — só a 1ª comercialização).
 */
export function calcRegimeFuturo(baseAnual, params) {
  const cbsRate = frac(params.cbsPlena2033);
  const ibsRate = frac(params.ibsSpPlena2033);

  const cbsDebito = baseAnual.faturamento * cbsRate;
  const cbsCreditoCogs = baseAnual.cogsTotal * cbsRate;
  const cbsCreditoDespesas = baseAnual.despesasElegiveis * cbsRate;
  const cbsLiquido = cbsDebito - cbsCreditoCogs - cbsCreditoDespesas;

  const ibsDebito = baseAnual.faturamento * ibsRate;
  const ibsCreditoCogs = baseAnual.cogsTotal * ibsRate;
  const ibsCreditoDespesas = baseAnual.despesasElegiveis * ibsRate;
  const ibsLiquido = ibsDebito - ibsCreditoCogs - ibsCreditoDespesas;

  const total = cbsLiquido + ibsLiquido;

  return {
    cbs: { debito: cbsDebito, creditoCogs: cbsCreditoCogs, creditoDespesas: cbsCreditoDespesas, liquido: cbsLiquido },
    ibs: { debito: ibsDebito, creditoCogs: ibsCreditoCogs, creditoDespesas: ibsCreditoDespesas, liquido: ibsLiquido },
    total,
    cbsRate,
    ibsRate,
  };
}

/** Bloco D (Simulação) — Impacto Líquido: Futuro vs. Atual. */
export function calcImpactoLiquido(regimeAtual, regimeFuturo, baseAnual) {
  const variacaoAbsoluta = regimeFuturo.total - regimeAtual.total;
  const variacaoPercentual = regimeAtual.total !== 0 ? variacaoAbsoluta / regimeAtual.total : 0;
  const creditoFuturoDespesas = baseAnual.despesasElegiveis * (regimeFuturo.cbsRate + regimeFuturo.ibsRate);
  const creditoAtualDespesas = baseAnual.despesasElegiveis * ALIQ_PIS_COFINS_ATUAL_REF;
  const creditoAdicional = creditoFuturoDespesas - creditoAtualDespesas;

  return { variacaoAbsoluta, variacaoPercentual, creditoAdicional };
}

/**
 * Cronograma legal de transição (LC 214/2025 / PLP 68/2024) — tabela fixa,
 * igual à aba PARÂMETROS (Bloco D) do Excel. Não é "célula amarela".
 */
export const TRANSICAO_SCHEDULE = [
  { ano: 2026, fase: "Teste", cbs: 0.009, ibs: 0.001, pisCofPct: 1.00, icmsIssPct: 1.00 },
  { ano: 2027, fase: "CBS Plena", cbs: 0.088, ibs: 0.001, pisCofPct: 0.00, icmsIssPct: 1.00 },
  { ano: 2028, fase: "Consolidação", cbs: 0.088, ibs: 0.001, pisCofPct: 0.00, icmsIssPct: 1.00 },
  { ano: 2029, fase: "Início da Transição", cbs: 0.088, ibs: 0.030, pisCofPct: 0.00, icmsIssPct: 0.80 },
  { ano: 2030, fase: "Transição", cbs: 0.088, ibs: 0.065, pisCofPct: 0.00, icmsIssPct: 0.60 },
  { ano: 2031, fase: "Aceleração", cbs: 0.088, ibs: 0.110, pisCofPct: 0.00, icmsIssPct: 0.35 },
  { ano: 2032, fase: "Fase Final", cbs: 0.088, ibs: 0.150, pisCofPct: 0.00, icmsIssPct: 0.10 },
  { ano: 2033, fase: "Regime Pleno", cbs: 0.088, ibs: 0.177, pisCofPct: 0.00, icmsIssPct: 0.00 },
];

/**
 * Aba "Transição Anual" — evolução ano a ano da carga tributária, aplicando
 * o cronograma legal sobre a margem tributável real (futuro) e sobre o
 * PIS/COFINS+ICMS/ISS líquidos reais (vigente, decrescente).
 *
 * O último ano (2033 — Regime Pleno) usa as alíquotas CBS/IBS ao vivo do
 * Bloco E (params), em vez do valor fixo do cronograma legal — assim, se o
 * usuário ajustar CBS/IBS em Parâmetros, o ano de regime pleno aqui bate com
 * o Dashboard e a Simulação.
 */
export function calcTransicaoAnual(baseAnual, regimeAtual, params) {
  const pisCofLiquido = regimeAtual.pis.liquido + regimeAtual.cofins.liquido;
  const icmsIssLiquido = regimeAtual.icms.liquido + regimeAtual.iss.liquido;
  const cbsPlena = params ? frac(params.cbsPlena2033) : null;
  const ibsPlena = params ? frac(params.ibsSpPlena2033) : null;

  return TRANSICAO_SCHEDULE.map((row, i) => {
    const isUltimoAno = i === TRANSICAO_SCHEDULE.length - 1;
    const cbs = isUltimoAno && cbsPlena !== null ? cbsPlena : row.cbs;
    const ibs = isUltimoAno && ibsPlena !== null ? ibsPlena : row.ibs;
    const futuro = baseAnual.margemTributavel * (cbs + ibs);
    const vigente = pisCofLiquido * row.pisCofPct + icmsIssLiquido * row.icmsIssPct;
    const total = futuro + vigente;
    return { ano: row.ano, fase: row.fase, futuro, vigente, total, cbs, ibs };
  });
}

/**
 * Aba "Impacto Caixa" — Bloco 1 (Volume Bruto Mensal) e Bloco 2 (Risco de
 * Capital de Giro). Fórmulas 1:1 com o Excel (médias mensais).
 */
export function calcImpactoCaixaFluxo(totalMensal, baseAnual, regimeFuturo, params) {
  const cbsRate = regimeFuturo.cbsRate;
  const ibsRate = regimeFuturo.ibsRate;
  const issRate = frac(params.issAtualServicos);

  // SAÍDA — débito bruto
  const atualFederal = (totalMensal.pisDebito + totalMensal.cofDebito) / 12;
  const futuroFederal = baseAnual.faturamento * cbsRate / 12;
  const atualEstadualMunicipal = totalMensal.icmsSaidas / 12 + (baseAnual.faturamento * PCT_RECEITA_SERVICOS * issRate) / 12;
  const futuroEstadualMunicipal = baseAnual.faturamento * ibsRate / 12;

  const saidaDebito = {
    label: "SAÍDA — Débito bruto total (impostos s/ receita)",
    atual: atualFederal + atualEstadualMunicipal,
    futuro: futuroFederal + futuroEstadualMunicipal,
    detalhes: [
      { label: "PIS + COFINS → CBS [federal]", atual: atualFederal, futuro: futuroFederal, variacao: futuroFederal - atualFederal },
      { label: "ICMS saídas + ISS → IBS [estadual/municipal]", atual: atualEstadualMunicipal, futuro: futuroEstadualMunicipal, variacao: futuroEstadualMunicipal - atualEstadualMunicipal },
      // Ata reunião (Diogo): IS só se aplica a indústrias/equiparados. O flag
      // vem do cadastro do projeto (atividadeIndustrial) injetado em params.
      params.isIndustrial
        ? { label: "IS (Imposto Seletivo) — indústria/equiparado (1ª comercialização)", atual: 0, futuro: 0, tag: "APLICA — verificar NCM" }
        : { label: "IS (Imposto Seletivo) — só indústria e importador recolhem (1ª comercialização)", atual: 0, futuro: 0, tag: "NÃO APLICA" },
    ],
  };
  saidaDebito.variacaoPercentual = saidaDebito.atual !== 0 ? saidaDebito.futuro / saidaDebito.atual - 1 : 0;

  // ENTRADA — crédito recuperado
  const atualCredFederal = (totalMensal.pisCredito + totalMensal.cofCredito) / 12;
  const futuroCredCogsCbs = baseAnual.cogsTotal * cbsRate / 12;
  const atualCredIcms = totalMensal.icmsCreditos / 12;
  const futuroCredCogsIbs = baseAnual.cogsTotal * ibsRate / 12;
  const futuroCredDespesas = baseAnual.despesasElegiveis * (cbsRate + ibsRate) / 12;

  const entradaCredito = {
    label: "ENTRADA — Crédito recuperado (compensação)",
    atual: atualCredFederal + atualCredIcms,
    futuro: futuroCredCogsCbs + futuroCredCogsIbs + futuroCredDespesas,
    detalhes: [
      { label: "PIS+COF crédito → CBS crédito COGS", atual: atualCredFederal, futuro: futuroCredCogsCbs, variacao: futuroCredCogsCbs - atualCredFederal },
      { label: "ICMS créditos entradas → IBS crédito COGS", atual: atualCredIcms, futuro: futuroCredCogsIbs, variacao: futuroCredCogsIbs - atualCredIcms },
      { label: "CBS+IBS crédito despesas operacionais (NOVO benefício)", atual: 0, futuro: futuroCredDespesas, tag: "Benefício novo" },
    ],
  };
  entradaCredito.variacaoPercentual = entradaCredito.atual !== 0 ? entradaCredito.futuro / entradaCredito.atual - 1 : 0;

  const pagamentoLiquidoAtual = saidaDebito.atual - entradaCredito.atual;
  const pagamentoLiquidoFuturo = saidaDebito.futuro - entradaCredito.futuro;
  const pagamentoLiquido = {
    atual: pagamentoLiquidoAtual,
    futuro: pagamentoLiquidoFuturo,
    variacaoPercentual: pagamentoLiquidoAtual !== 0 ? pagamentoLiquidoFuturo / pagamentoLiquidoAtual - 1 : 0,
  };

  const riscoMetricas = [
    {
      label: "Volume crédito tributário/mês (máxima exposição)",
      atual: entradaCredito.atual,
      futuro: entradaCredito.futuro,
      multiplicador: entradaCredito.atual !== 0 ? entradaCredito.futuro / entradaCredito.atual : 0,
    },
    {
      label: "Débito bruto mensal (obrigação fiscal bruta)",
      atual: saidaDebito.atual,
      futuro: saidaDebito.futuro,
      multiplicador: saidaDebito.atual !== 0 ? saidaDebito.futuro / saidaDebito.atual : 0,
    },
    {
      label: "Pagamento líquido mensal (caixa real)",
      atual: pagamentoLiquido.atual,
      futuro: pagamentoLiquido.futuro,
      multiplicador: pagamentoLiquido.atual !== 0 ? pagamentoLiquido.futuro / pagamentoLiquido.atual : 0,
    },
  ];

  return { saidaDebito, entradaCredito, pagamentoLiquido, riscoMetricas };
}

/**
 * Aba "Impacto Caixa" — Bloco 3/4 (Segmentos + Foco Oficina). A divisão
 * 65% Veículos / 25% Peças / 10% Oficina é fixa no Excel (só o % Veículos é
 * ajustável — Peças absorve o residual, Oficina fica fixo nos 10% usados
 * também no cálculo de ISS).
 */
export function calcImpactoCaixaSegmentos(totalMensal, baseAnual, regimeFuturo, params) {
  const cbsRate = regimeFuturo.cbsRate;
  const ibsRate = regimeFuturo.ibsRate;
  const taxaFutura = cbsRate + ibsRate;
  const faturamentoMes = baseAnual.faturamento / 12;

  const pctVeiculos = frac(params.pctReceitaVeiculos);
  const pctOficina = PCT_RECEITA_SERVICOS;
  const pctPecas = Math.max(1 - pctVeiculos - pctOficina, 0);

  // Veículos: dealer não recolhe ICMS-ST/PIS-COFINS monofásico na ponta (~0% hoje);
  // no futuro, CBS+IBS incide sobre a margem (receita - COGS veículos).
  const receitaVeiculosMes = faturamentoMes * pctVeiculos;
  const margemVeiculosMes = receitaVeiculosMes * (1 - frac(params.pctCogsVeiculos));
  const veiculos = {
    id: "veiculos",
    nome: "Produtos com ST ou monofásicos",
    participacao: `${(pctVeiculos * 100).toFixed(0)}% da receita`,
    contexto: "ICMS-ST atual",
    cargaAtualMes: 0,
    cargaFuturaMes: margemVeiculosMes * taxaFutura,
  };
  veiculos.deltaMensal = veiculos.cargaFuturaMes - veiculos.cargaAtualMes;

  // Peças: ICMS 18% normal com crédito + PIS/COFINS 9,25% sobre a margem hoje;
  // no futuro, CBS+IBS sobre a margem (receita - COGS peças) com crédito amplo.
  const receitaPecasMes = faturamentoMes * pctPecas;
  const margemPecasMes = receitaPecasMes * (1 - frac(params.pctCogsPecasServ));
  const pecas = {
    id: "pecas",
    nome: "Demais produtos",
    participacao: `${(pctPecas * 100).toFixed(0)}% da receita`,
    contexto: "ICMS normal c/ crédito",
    cargaAtualMes: margemPecasMes * (0.18 + ALIQ_PIS_COFINS_ATUAL_REF),
    cargaFuturaMes: margemPecasMes * taxaFutura,
  };
  pecas.deltaMensal = pecas.cargaFuturaMes - pecas.cargaAtualMes;

  // Oficina — segmento crítico: ISS 5% hoje x CBS+IBS sobre receita bruta
  // (mão de obra não credita) amanhã.
  const receitaOficinaMes = faturamentoMes * pctOficina;
  const maoDeObraMes = receitaOficinaMes * PCT_MAO_DE_OBRA_OFICINA;
  const materiaisMes = receitaOficinaMes * PCT_MATERIAIS_OFICINA;
  const issAtualMes = receitaOficinaMes * frac(params.issAtualServicos);
  const cbsIbsBrutoMes = receitaOficinaMes * taxaFutura;
  const creditoMateriaisMes = materiaisMes * taxaFutura;
  const cargaFuturaOficinaMes = cbsIbsBrutoMes - creditoMateriaisMes;

  const oficina = {
    id: "oficina",
    nome: "Serviços",
    participacao: `${(pctOficina * 100).toFixed(0)}% da receita`,
    contexto: "ISS → CBS+IBS",
    cargaAtualMes: issAtualMes,
    cargaFuturaMes: cargaFuturaOficinaMes,
    destaque: true,
  };
  oficina.deltaMensal = oficina.cargaFuturaMes - oficina.cargaAtualMes;

  const oficinaDetalhe = {
    receitaMes: receitaOficinaMes,
    maoDeObraMes,
    materiaisMes,
    issAtualMes,
    cbsIbsBrutoMes,
    creditoMateriaisMes,
    cargaFuturaOficinaMes,
    taxaEfetivaAtual: receitaOficinaMes !== 0 ? issAtualMes / receitaOficinaMes : 0,
    taxaEfetivaFutura: receitaOficinaMes !== 0 ? cargaFuturaOficinaMes / receitaOficinaMes : 0,
    aumentoAnual: (cargaFuturaOficinaMes - issAtualMes) * 12,
  };

  return { segmentos: [veiculos, pecas, oficina], oficinaDetalhe };
}

/**
 * Aba "Formação de Preço" — fórmulas de markup por dentro (não-cumulativo),
 * exatamente como na Seção 3/4/5 do Excel.
 *
 * inputs: { custo, pctFrete, pctMateriais (só oficina), pctComissao, pctOpex, pctMargem }
 */
export function calcFormacaoPreco(segmentId, inputs, params) {
  const cbsRate = frac(params.cbsPlena2033);
  const ibsRate = frac(params.ibsSpPlena2033);
  const taxaFutura = cbsRate + ibsRate;
  const issRate = frac(params.issAtualServicos);
  const pctDespesasElegiveis = inputs.despesasElegiveis ?? PCT_DESPESAS_ELEGIVEIS;

  const custoTotal = inputs.custo * (1 + (inputs.pctFrete || 0));
  const comm = inputs.pctComissao || 0;
  const opex = inputs.pctOpex || 0;
  const ml = inputs.pctMargem || 0;

  let pvAtual;
  let pvFuturo;
  let cargaAtualPct;
  let cargaFuturaPct;

  if (segmentId === "oficina") {
    // ATUAL: PV = CT ÷ (1 − ISS − Comm − OpEx − ML)
    const denomAtual = 1 - issRate - comm - opex - ml;
    pvAtual = denomAtual > 0 ? custoTotal / denomAtual : null;

    // FUTURO: PV = CT×(1 − T×%Mat) ÷ (1 − T×(1 − Elig×OpEx) − Comm − OpEx − ML)
    const pctMateriais = inputs.pctMateriais || 0;
    const denomFuturo = 1 - taxaFutura * (1 - pctDespesasElegiveis * opex) - comm - opex - ml;
    pvFuturo = denomFuturo > 0 ? (custoTotal * (1 - taxaFutura * pctMateriais)) / denomFuturo : null;

    cargaAtualPct = pvAtual ? issRate : null;
    cargaFuturaPct = pvFuturo ? taxaFutura * (1 - pctMateriais) : null;
  } else {
    // Peças e Veículos — não-cumulativo. Peças usa T_atual = ICMS+PIS+COFINS;
    // Veículos usa T_atual = 0 (monofásico / ICMS-ST não recolhido pelo dealer).
    const tAtual = segmentId === "veiculos" ? 0 : 0.18 + ALIQ_PIS_COFINS_ATUAL_REF;

    const denomAtual = 1 - tAtual - comm - opex - ml;
    pvAtual = denomAtual > 0 ? (custoTotal * (1 - tAtual)) / denomAtual : null;

    const denomFuturo = 1 - taxaFutura * (1 - pctDespesasElegiveis * opex) - comm - opex - ml;
    pvFuturo = denomFuturo > 0 ? (custoTotal * (1 - taxaFutura)) / denomFuturo : null;

    cargaAtualPct = tAtual;
    cargaFuturaPct = taxaFutura;
  }

  const markupDivisorAtual = pvAtual ? custoTotal / pvAtual : null;
  const markupDivisorFuturo = pvFuturo ? custoTotal / pvFuturo : null;
  const markupMultAtual = pvAtual ? pvAtual / custoTotal : null;
  const markupMultFuturo = pvFuturo ? pvFuturo / custoTotal : null;

  const cargaLiquidaAtual = pvAtual ? (pvAtual - custoTotal - comm * pvAtual - opex * pvAtual - ml * pvAtual) / pvAtual : null;
  const cargaLiquidaFuturo = pvFuturo ? (pvFuturo - custoTotal - comm * pvFuturo - opex * pvFuturo - ml * pvFuturo) / pvFuturo : null;

  return {
    custoTotal,
    pvAtual,
    pvFuturo,
    deltaPct: pvAtual ? (pvFuturo ? pvFuturo / pvAtual - 1 : null) : null,
    markupDivisorAtual,
    markupDivisorFuturo,
    markupMultAtual,
    markupMultFuturo,
    cargaLiquidaAtual,
    cargaLiquidaFuturo,
    comissaoAtual: pvAtual ? comm * pvAtual : null,
    comissaoFuturo: pvFuturo ? comm * pvFuturo : null,
    opexAtual: pvAtual ? opex * pvAtual : null,
    opexFuturo: pvFuturo ? opex * pvFuturo : null,
    margemAtual: pvAtual ? ml * pvAtual : null,
    margemFuturo: pvFuturo ? ml * pvFuturo : null,
    cargaAtualPct,
    cargaFuturaPct,
  };
}

/**
 * Caminho inverso de calcFormacaoPreco: dado um preço de venda, qual margem líquida
 * sobra em cada regime. Só isola ML nas mesmas fórmulas. Entrar com a margem devolvida
 * em calcFormacaoPreco resulta exatamente no preço informado.
 *
 * inputs: { custo, pctFrete, pctMateriais (só oficina), pctComissao, pctOpex }
 */
export function calcMargemPorPreco(segmentId, inputs, params, preco) {
  if (!(preco > 0)) return { margemAtual: null, margemFutura: null };
  const taxaFutura = frac(params.cbsPlena2033) + frac(params.ibsSpPlena2033);
  const custoTotal = inputs.custo * (1 + (inputs.pctFrete || 0));
  const comm = inputs.pctComissao || 0;
  const opex = inputs.pctOpex || 0;
  const oficina = segmentId === "oficina";
  const tAtual = segmentId === "veiculos" ? 0 : 0.18 + ALIQ_PIS_COFINS_ATUAL_REF;

  const margemAtual = oficina
    ? 1 - frac(params.issAtualServicos) - comm - opex - custoTotal / preco
    : 1 - tAtual - comm - opex - (custoTotal * (1 - tAtual)) / preco;

  const creditoCusto = oficina ? taxaFutura * (inputs.pctMateriais || 0) : taxaFutura;
  const margemFutura = 1 - taxaFutura * (1 - (inputs.despesasElegiveis ?? PCT_DESPESAS_ELEGIVEIS) * opex) - comm - opex - (custoTotal * (1 - creditoCusto)) / preco;

  return { margemAtual, margemFutura };
}

/**
 * Imposto da formação de preço, aberto em débito e créditos. O líquido é a carga da
 * fórmula por dentro, em cada regime e no preço informado.
 */
export function calcImpostoAberto(segmentId, inputs, params, precoAtual, precoFuturo) {
  const taxaFutura = frac(params.cbsPlena2033) + frac(params.ibsSpPlena2033);
  const custoTotal = inputs.custo * (1 + (inputs.pctFrete || 0));
  const opex = inputs.pctOpex || 0;
  const oficina = segmentId === "oficina";
  const tAtual = oficina ? frac(params.issAtualServicos) : (segmentId === "veiculos" ? 0 : 0.18 + ALIQ_PIS_COFINS_ATUAL_REF);
  const parcelaComCredito = oficina ? (inputs.pctMateriais || 0) : 1;

  return {
    atual: {
      debito: tAtual * precoAtual,
      creditoCusto: oficina ? 0 : tAtual * custoTotal,
      creditoDespesas: 0,
    },
    futuro: {
      debito: taxaFutura * precoFuturo,
      creditoCusto: taxaFutura * parcelaComCredito * custoTotal,
      creditoDespesas: taxaFutura * (inputs.despesasElegiveis ?? PCT_DESPESAS_ELEGIVEIS) * opex * precoFuturo,
    },
  };
}

/**
 * A conta de um preço em cada ano da transição, para preço e custo mantidos. Aplica o
 * cronograma legal às alíquotas do segmento: a parcela ainda vigente dos tributos atuais
 * e as alíquotas de CBS/IBS do ano. Em 2033, usa as alíquotas ao vivo dos Parâmetros,
 * como calcTransicaoAnual, e o resultado coincide com o regime futuro.
 */
export function calcPrecoPorAno(segmentId, inputs, params, preco) {
  const custoTotal = inputs.custo * (1 + (inputs.pctFrete || 0));
  const comm = inputs.pctComissao || 0;
  const opex = inputs.pctOpex || 0;
  const oficina = segmentId === "oficina";
  const veiculos = segmentId === "veiculos";
  const parcelaComCredito = oficina ? (inputs.pctMateriais || 0) : 1;
  const ultimo = TRANSICAO_SCHEDULE.length - 1;

  return TRANSICAO_SCHEDULE.map((row, i) => {
    const cbs = i === ultimo ? frac(params.cbsPlena2033) : row.cbs;
    const ibs = i === ultimo ? frac(params.ibsSpPlena2033) : row.ibs;
    const taxa = cbs + ibs;

    // Tributos que saem: oficina paga ISS sem crédito; peças, ICMS e PIS/COFINS com crédito
    let vigente = 0;
    if (oficina) {
      vigente = frac(params.issAtualServicos) * row.icmsIssPct * preco;
    } else if (!veiculos) {
      const aliquota = 0.18 * row.icmsIssPct + ALIQ_PIS_COFINS_ATUAL_REF * row.pisCofPct;
      vigente = aliquota * (preco - custoTotal);
    }

    const debitoNovo = taxa * preco;
    const creditoCusto = taxa * parcelaComCredito * custoTotal;
    const creditoDespesas = taxa * (inputs.despesasElegiveis ?? PCT_DESPESAS_ELEGIVEIS) * opex * preco;
    const comissao = comm * preco;
    const despesas = opex * preco;
    const impostoLiquido = vigente + debitoNovo - creditoCusto - creditoDespesas;

    return {
      ano: row.ano,
      fase: row.fase,
      vigente,
      debitoNovo,
      creditoCusto,
      creditoDespesas,
      comissao,
      despesas,
      impostoLiquido,
      margem: preco - custoTotal - impostoLiquido - comissao - despesas,
    };
  });
}

/** Tabela de sensibilidade (Seção 6) — só para o segmento Peças. */
export function calcSensibilidadePecas(custos, margens, params, opexParams) {
  const linhas = [];
  custos.forEach((custo) => {
    const atualValores = margens.map((m) =>
      calcFormacaoPreco("pecas", { custo, pctFrete: opexParams.pctFrete, pctComissao: opexParams.pctComissao, pctOpex: opexParams.pctOpex, pctMargem: m / 100 }, params).pvAtual
    );
    const futuroValores = margens.map((m) =>
      calcFormacaoPreco("pecas", { custo, pctFrete: opexParams.pctFrete, pctComissao: opexParams.pctComissao, pctOpex: opexParams.pctOpex, pctMargem: m / 100 }, params).pvFuturo
    );
    linhas.push({ custo, regime: "ATUAL", valores: atualValores });
    linhas.push({ custo, regime: "FUTURO", valores: futuroValores });
  });
  return linhas;
}
